/**
 * The mech equip pickers' entries: the MECH sheet's EDIT mode (`MechSheetEdit.tsx`) builds its
 * `CompendiumExplorer` rows here.
 */
import type { Compendium } from "@lancer-vtt/compendium";
import type { LicensedItem, MechData, PilotData, SuperheavyBraceState, TalentGatedItem } from "@lancer-vtt/domain";
import type { ExplorerEntry } from "../../lib/editor";
import { licenseLock } from "../../lib/editor/licenseAffordance";
import { loadoutPickLock, type LoadoutCandidate } from "../../lib/editor/loadoutPickLock";
import { frameIntegratedLock, integratedLock, talentLock } from "../../lib/editor/talentAffordance";

/**
 * Build an explorer entry for a licensed item: name + a meta line (SP / mount), plus a
 * `locked` marker when the pilot hasn't earned its license (mirroring `meetsLicenseRequirement`),
 * OR hasn't earned the talent rank a talent-exclusive item requires (mirroring
 * `meetsTalentRequirement` via `talentLock` — Ammo Cases, the Technophile NHPs, the Prototype
 * Weapons, the Fuel Rod Gun), OR is talent-**integrated** and so never manually equippable at
 * any rank (mirroring `isIntegratedTalentItem` via `integratedLock` — Prototype Weapons /
 * Fuel Rod Gun / Ammo Case — the reducer wall blocks both `applyEquipWeapon` and
 * `applyEquipSystem`, so the picker greys both), OR is **frame**-integrated — hard-mounted by some chassis's `integrated[]`
 * list and so never manually equippable on any frame (mirroring `isFrameIntegratedItem` via
 * `frameIntegratedLock` — ZF4 SOLIDCORE, Apocalypse Rail, Latch Drone, M35 Mjolnir; these carry
 * no license/talent link at all, so nothing above catches them), OR when adding it would
 * bust a **loadout invariant** (mirroring `checkLoadoutInvariants` via `loadoutPickLock` — a 2nd
 * Unique weapon/system/mod, a 2nd AI system, or a Superheavy bracing conflict). Any lock greys
 * the row, sorts it after available items, and disables ADD. A frame has no loadout candidate,
 * so only the license/talent gates apply there (frames carry no `talent_id` today, but the check
 * is harmless if one ever did).
 */
export function equipEntry(args: {
  pilot: PilotData;
  mech: MechData;
  compendium: Compendium;
  item: { id: string; name?: string };
  type: "frame" | "weapon" | "system" | "mod";
  /** The would-be addition this row represents (absent for frames — no Unique/AI surface). */
  candidate?: LoadoutCandidate;
  extraMeta?: string;
}): ExplorerEntry {
  const { pilot, mech, compendium, item, type, candidate, extraMeta } = args;
  const lock =
    licenseLock(pilot, item as LicensedItem, type) ??
    talentLock(pilot, item as TalentGatedItem, compendium) ??
    (type === "weapon" || type === "system"
      ? (integratedLock(item as TalentGatedItem & { id: string }, compendium) ??
        frameIntegratedLock(item.id, compendium))
      : null) ??
    (candidate ? loadoutPickLock({ pilot, mech, candidate, compendium }) : null);
  return {
    id: item.id,
    name: item.name ?? item.id,
    meta: extraMeta || undefined,
    locked: lock ? { reason: lock.label } : undefined,
  };
}

/** Name the Superheavy weapon(s) a reserved brace is holding for, for the RESERVED tooltip. */
export function braceWeaponLabel(brace: SuperheavyBraceState, compendium: Compendium): string {
  if (brace.required === 1) {
    const w = compendium.getById("weapon", brace.superheavyIds[0]!);
    return (w?.name as string | undefined) ?? "a Superheavy weapon";
  }
  return "your Superheavy weapons";
}
